import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, useChannelStateContext, useChatContext } from 'stream-chat-react';

import './MessagingChannelHeader.css';

import { getCleanImage, HamburgerIcon } from '../../assets';

import type { ChannelMemberResponse } from 'stream-chat';

const AvatarGroup = ({ members }: { members: ChannelMemberResponse[] }) => {
  if (members.length === 1) {
    return (
      <div className='messaging__channel-header__avatars'>
        <Avatar image={getCleanImage()} name={members[0]?.user?.name} />
      </div>
    );
  }

  if (members.length === 2) {
    return (
      <div className='messaging__channel-header__avatars two'>
        <span>
          <Avatar image={getCleanImage()} name={members[0]?.user?.name} />
        </span>
        <span>
          <Avatar image={getCleanImage()} name={members[1]?.user?.name} />
        </span>
      </div>
    );
  }

  if (members.length >= 3) {
    return (
      <div className='messaging__channel-header__avatars three'>
        <span>
          <Avatar image={getCleanImage()} name={members[0]?.user?.name} />
        </span>
        <span>
          <Avatar image={getCleanImage()} className='avatar-small' name={members[1]?.user?.name} />
          <Avatar image={getCleanImage()} className='avatar-small' name={members[2]?.user?.name} />
        </span>
      </div>
    );
  }

  return null;
};

type Props = {
  theme: string;
  toggleMobile: () => void;
};

const MessagingChannelHeader: React.FC<Props> = (props) => {
  const { theme, toggleMobile } = props;

  const { client } = useChatContext();

  const { channel, members: memberState } = useChannelStateContext();

  const [channelName, setChannelName] = useState(channel.data?.name || '');
  const [isEditing, setIsEditing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const members = useMemo(
    () =>
      Object.values(memberState || {}).filter(
        (member) => member.user?.id !== client?.user?.id,
      ),
    [memberState, client?.user?.id],
  );

  const title = useMemo(() => {
    if (channelName) return channelName;
    if (members.length > 0) {
      return members
        .map((member) => member.user?.name || member.user?.id || 'Unnamed User')
        .join(', ');
    }
    return 'Chat';
  }, [channelName, members]);

  const updateChannel = async () => {
    if (channelName && channelName !== channel.data?.name) {
      await channel.update(
        { name: channelName },
        { text: `Channel name changed to ${channelName}` },
      );
    }

    setIsEditing(false);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef?.current.focus();
    }
  }, [isEditing]);

  // Sync channel name when switching channels
  useEffect(() => {
    setChannelName(channel.data?.name || '');
  }, [channel.id]); // eslint-disable-line

  const EditHeader = () => (
    <form
      style={{ flex: 1 }}
      onSubmit={(event) => {
        event.preventDefault();
        inputRef?.current?.blur();
      }}
    >
      <input
        autoFocus
        className='channel-header__edit-input'
        onBlur={updateChannel}
        onChange={(event) => setChannelName(event.target.value)}
        placeholder='Type a new name for the chat'
        ref={inputRef}
        value={channelName}
      />
    </form>
  );

  return (
    <div className='messaging__channel-header'>
      <div id='mobile-nav-icon' className={`${theme}`} onClick={() => toggleMobile()}>
        <HamburgerIcon />
      </div>
      <AvatarGroup members={members} />
      {!isEditing ? (
        <div className='channel-header__name'>{title}</div>
      ) : (
        <EditHeader />
      )}
    </div>
  );
};

export default React.memo(MessagingChannelHeader);
